import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { HashLink } from 'react-router-hash-link';
import { NAV_MEGA_ITEMS } from '../data/navMegaMenu';
import { getServiceData, getMaxillaryServiceData } from '../Pages/ServiceData';
import { useLocale } from '../context/LocaleContext';

function MegaMenuLink({ item, className, onNavigate, children, listOnly = false }) {
  const content = children ?? (
    <>
      <span className="nav_mega_link_label">{item.label}</span>
      {!listOnly && item.desc ? (
        <span className="nav_mega_link_desc">{item.desc}</span>
      ) : null}
    </>
  );

  if (item.external) {
    return (
      <a className={className} href={item.to} onClick={onNavigate}>
        {content}
      </a>
    );
  }

  if (item.hash) {
    return (
      <HashLink smooth to={item.to} className={className} onClick={onNavigate}>
        {content}
      </HashLink>
    );
  }

  return (
    <Link to={item.to} className={className} onClick={onNavigate}>
      {content}
    </Link>
  );
}

function buildColumns(item, t) {
  return item.columns.map((col) => ({
    title: t(col.titleKey),
    items: col.items.map((link) => ({
      ...link,
      label: t(link.labelKey),
      desc: link.descKey ? t(link.descKey) : '',
    })),
  }));
}

function NavMegaTreatmentSlots({ locale, onNavigate, isMobile, treatmentSource = 'orthodontics' }) {
  const treatments = useMemo(() => {
    if (treatmentSource === 'maxillary') {
      return getMaxillaryServiceData(locale);
    }
    return getServiceData(locale);
  }, [locale, treatmentSource]);

  return (
    <div
      className={`nav_mega_treatment_grid${isMobile ? ' nav_mega_treatment_grid--mobile' : ''}`}
    >
      {treatments.map((item) => (
        <Link
          key={item.pageId}
          to={item.to}
          className="nav_mega_treatment_card"
          onClick={onNavigate}
        >
          <img
            className={`nav_mega_treatment_img${
              item.imageIsPhoto ? ' nav_mega_treatment_img--photo' : ''
            }${item.pageId === 'miniImplants' ? ' nav_mega_treatment_img--micro' : ''}`}
            src={item.img_src}
            alt=""
          />
          <span className="nav_mega_treatment_title">{item.title}</span>
          <span className="nav_mega_treatment_desc">{item.description}</span>
        </Link>
      ))}
    </div>
  );
}

function NavMegaPanel({ item, t, locale, onNavigate, isMobile = false, singleColumn = false }) {
  const [openCols, setOpenCols] = useState(() => new Set());

  if (item.layout === 'treatmentCards') {
    const panelTitleKey =
      item.treatmentSource === 'maxillary'
        ? 'navMega.maxillaryOrtho.colTreatments'
        : 'services.header';

    return (
      <div
        className={`nav_mega_panel nav_mega_panel--treatments${
          isMobile ? ' nav_mega_panel--mobile' : ''
        }`}
        role="region"
        aria-label={t(item.labelKey)}
      >
        <div className="nav_mega_panel_inner nav_mega_panel_inner--treatments page-shell">
          <p className="nav_mega_col_title nav_mega_col_title--treatments">
            {t(panelTitleKey)}
          </p>
          <NavMegaTreatmentSlots
            locale={locale}
            onNavigate={onNavigate}
            isMobile={isMobile}
            treatmentSource={item.treatmentSource || 'orthodontics'}
          />
        </div>
      </div>
    );
  }

  const columns = buildColumns(item, t);
  const isSingleColumn = singleColumn || item.singleColumn;

  const toggleCol = (title) => {
    setOpenCols((prev) => {
      const next = new Set(prev);
      if (next.has(title)) next.delete(title);
      else next.add(title);
      return next;
    });
  };

  if (isSingleColumn && columns.length === 1) {
    const col = columns[0];
    return (
      <div
        className={`nav_mega_panel nav_mega_panel--single${isMobile ? ' nav_mega_panel--mobile' : ''}`}
        role="region"
        aria-label={t(item.labelKey)}
      >
        <ul className={`nav_mega_col_list${isMobile ? ' nav_mega_col_list--flat' : ''}`}>
          {col.items.map((link) => (
            <li key={link.labelKey}>
              <MegaMenuLink
                item={link}
                className={`nav_mega_link${isMobile ? ' nav_mobile_row nav_mobile_row--link' : ''}`}
                onNavigate={onNavigate}
                listOnly={isMobile}
              />
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (isMobile) {
    return (
      <div className="nav_mega_panel nav_mega_panel--mobile" role="region" aria-label={t(item.labelKey)}>
        <div className="nav_mega_panel_inner nav_mega_panel_inner--mobile">
          {columns.map((col) => {
            const colOpen = openCols.has(col.title);
            return (
              <div
                key={col.title}
                className={`nav_mega_col nav_mega_col--accordion${colOpen ? ' is-open' : ''}`}
              >
                <button
                  type="button"
                  className="nav_mega_col_trigger nav_mobile_row nav_mobile_row--sub"
                  aria-expanded={colOpen ? 'true' : 'false'}
                  onClick={() => toggleCol(col.title)}
                >
                  <span className="nav_mega_col_trigger_label">{col.title}</span>
                  <i className="fa fa-chevron-right nav_mega_col_chevron" aria-hidden="true" />
                </button>
                <div className="nav_mega_col_body" hidden={!colOpen}>
                  <ul className="nav_mega_col_list nav_mega_col_list--flat">
                    {col.items.map((link) => (
                      <li key={link.labelKey}>
                        <MegaMenuLink
                          item={link}
                          className="nav_mega_link nav_mobile_row nav_mobile_row--link"
                          onNavigate={onNavigate}
                          listOnly
                        />
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="nav_mega_panel" role="region" aria-label={t(item.labelKey)}>
      <div
        className={`nav_mega_panel_inner page-shell${
          item.columnLayout === 'two' ? ' nav_mega_panel_inner--two' : ''
        }`}
      >
        {columns.map((col) => (
          <div key={col.title} className="nav_mega_col">
            <p className="nav_mega_col_title">{col.title}</p>
            <ul className="nav_mega_col_list">
              {col.items.map((link) => (
                <li key={link.labelKey}>
                  <MegaMenuLink item={link} className="nav_mega_link" onNavigate={onNavigate} />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function NavMegaMenu({ onNavigate, variant = 'desktop' }) {
  const { locale, t } = useLocale();
  const location = useLocation();
  const isMobileVariant = variant === 'mobile';
  const [openId, setOpenId] = useState(null);
  const [desktopNav, setDesktopNav] = useState(() => {
    if (isMobileVariant) return false;
    return typeof window !== 'undefined' ? window.matchMedia('(min-width: 901px)').matches : true;
  });
  const rootRef = useRef(null);
  const containerRef = useRef(null);
  const closeTimerRef = useRef(null);

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleClose = useCallback(() => {
    if (!desktopNav) return;
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => setOpenId(null), 140);
  }, [desktopNav]);

  const openOnHover = (id) => {
    if (!desktopNav) return;
    clearCloseTimer();
    setOpenId(id);
  };

  const handleNavigate = () => {
    setOpenId(null);
    onNavigate?.();
  };

  useEffect(() => {
    containerRef.current = rootRef.current?.closest('.nav-container') ?? null;
  }, []);

  useEffect(() => {
    if (isMobileVariant) {
      setDesktopNav(false);
      return undefined;
    }
    const mq = window.matchMedia('(min-width: 901px)');
    const sync = () => setDesktopNav(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, [isMobileVariant]);

  useEffect(() => {
    setOpenId(null);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    if (!openId) return undefined;
    const onDocDown = (e) => {
      const root = rootRef.current;
      const container = containerRef.current;
      if (root?.contains(e.target)) return;
      if (container?.querySelector('.nav_mega_panel_wrap')?.contains(e.target)) return;
      setOpenId(null);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpenId(null);
    };
    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [openId]);

  useEffect(() => () => clearCloseTimer(), []);

  const toggleItem = (id) => {
    setOpenId((prev) => (prev === id ? null : id));
  };

  const openItem = NAV_MEGA_ITEMS.find((item) => item.id === openId && !item.isLink);
  const openItemIsDropdown = Boolean(openItem?.singleColumn);

  const desktopPanel =
    openItem && desktopNav && !openItemIsDropdown && containerRef.current
      ? createPortal(
          <div
            className="nav_mega_panel_wrap"
            onMouseEnter={clearCloseTimer}
            onMouseLeave={scheduleClose}
          >
            <NavMegaPanel item={openItem} t={t} locale={locale} onNavigate={handleNavigate} isMobile={false} />
          </div>,
          containerRef.current
        )
      : null;

  return (
    <>
      <nav
        ref={rootRef}
        className={`nav_mega${isMobileVariant ? ' nav_mega--mobile' : ''}`}
        aria-label={t('navMega.ariaLabel')}
      >
        {NAV_MEGA_ITEMS.map((item) => {
          if (item.isLink) {
            const active = location.pathname === item.to;
            return (
              <div key={item.id} className="MenuItems nav_mega_item nav_mega_item--link">
                <Link
                  to={item.to}
                  className={active ? 'nav_link--active' : undefined}
                  onClick={handleNavigate}
                  aria-current={active ? 'page' : undefined}
                >
                  {t(item.labelKey)}
                </Link>
              </div>
            );
          }

          const isOpen = openId === item.id;
          const isDropdown = Boolean(item.singleColumn);

          return (
            <div
              key={item.id}
              className={`MenuItems nav_mega_item${isOpen ? ' is-open' : ''}${isDropdown ? ' nav_mega_item--dropdown' : ''}`}
              onMouseEnter={() => openOnHover(item.id)}
              onMouseLeave={scheduleClose}
            >
              <button
                type="button"
                className={`nav_mega_trigger${isMobileVariant ? ' nav_mobile_row' : ''}`}
                aria-expanded={isOpen ? 'true' : 'false'}
                aria-haspopup="true"
                onClick={() => toggleItem(item.id)}
              >
                <span className="nav_mega_trigger_label">{t(item.labelKey)}</span>
                {isMobileVariant ? (
                  <i
                    className={`fa fa-chevron-right nav_mega_trigger_chevron${isOpen ? ' is-expanded' : ''}`}
                    aria-hidden="true"
                  />
                ) : (
                  <i className={`fa fa-chevron-${isOpen ? 'up' : 'down'}`} aria-hidden="true" />
                )}
              </button>
              {isOpen && desktopNav && isDropdown ? (
                <div
                  className="nav_mega_dropdown"
                  onMouseEnter={clearCloseTimer}
                  onMouseLeave={scheduleClose}
                >
                  <NavMegaPanel item={item} t={t} locale={locale} onNavigate={handleNavigate} singleColumn />
                </div>
              ) : null}
              {isOpen && !desktopNav ? (
                <NavMegaPanel
                  item={item}
                  t={t}
                  locale={locale}
                  onNavigate={handleNavigate}
                  isMobile
                  singleColumn={isDropdown}
                />
              ) : null}
            </div>
          );
        })}
      </nav>
      {desktopPanel}
    </>
  );
}
